/**
 * Cómo se lee y se pinta cada estado de una tarea. Fuente única.
 *
 * El valor guardado y la etiqueta que se lee son cosas distintas, y acá es
 * donde se separan. Los valores (`en_progreso`, `completada`, `bloqueada`) los
 * leen quince pantallas, los bots de WhatsApp que crean tareas, el backlog que
 * consume el agente de FUTURA y la facturación por tareas: cambiarlos en la
 * base por un cambio de vocabulario habría roto las cuatro cosas a la vez. Así
 * que se cambió solo lo que se lee.
 *
 * `cancelada` existe y tiene tareas detrás, pero **no es una columna del
 * tablero**: cancelar no es un paso del trabajo, es sacarlo de la fila. Una
 * columna permanente para eso ocupa el mismo ancho que las cinco que sí
 * cuentan, y el tablero se lee peor por algo que casi nunca pasa. Sigue
 * siendo elegible desde el selector de la tabla y de la ficha.
 */
import type { EstadoTarea } from './types'
import type { BadgeVariant } from '@/components/ui'

export interface EstadoMeta {
  /** Lo que se lee en pantalla. */
  label: string
  variant: BadgeVariant
  /** Color del punto y de la cabecera de su columna en el Kanban. */
  color: string
  /** No arrastra trabajo: la tarea ya salió del tablero. */
  terminal?: boolean
}

export const ESTADO_TAREA_META: Record<EstadoTarea, EstadoMeta> = {
  nuevo: { label: 'Nuevo', variant: 'info', color: 'var(--primary-light)' },
  pendiente: { label: 'Pendiente', variant: 'neutral', color: 'var(--text-secondary)' },
  en_progreso: { label: 'En curso', variant: 'warning', color: 'var(--warning)' },
  completada: { label: 'Listo', variant: 'activo', color: 'var(--success)', terminal: true },
  bloqueada: { label: 'Estancado', variant: 'danger', color: 'var(--danger)' },
  cancelada: { label: 'Cancelada', variant: 'neutral', color: 'var(--text-muted)', terminal: true },
}

/**
 * Las columnas del Kanban: de lo que no empezó a lo que terminó. Sin
 * `cancelada`, que no es un paso sino una salida.
 */
export const ESTADOS_TABLERO: EstadoTarea[] = [
  'nuevo', 'pendiente', 'en_progreso', 'bloqueada', 'completada',
]

/**
 * Lo que se puede elegir en un selector: el tablero **más** `cancelada`.
 *
 * Son dos listas y no una con un filtro porque responden preguntas distintas:
 * «qué columnas dibujo» y «a qué estados puedo mover esto». Cancelar tiene
 * que seguir siendo posible aunque no haya dónde soltarla.
 */
export const ESTADOS_ASIGNABLES: EstadoTarea[] = [...ESTADOS_TABLERO, 'cancelada']

export function metaDeEstado(estado: string | null | undefined): EstadoMeta {
  return ESTADO_TAREA_META[(estado || 'pendiente') as EstadoTarea]
    ?? { label: estado || 'Sin estado', variant: 'neutral', color: 'var(--text-muted)' }
}

/** dd MMM — sin año cuando es de este año, que es el caso normal. */
export function fechaCortaTarea(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`)
  const esteAnio = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleDateString('es-PE', {
    day: '2-digit',
    month: 'short',
    ...(esteAnio ? {} : { year: 'numeric' }),
  })
}

/**
 * El rango dicho como lo diría una persona: «4 – 8 nov» y no dos fechas
 * sueltas. Sin inicio es una fecha sola; sin ninguna de las dos, nada.
 */
export function rangoDeFechas(inicio: string | null, limite: string | null): string {
  if (inicio && limite) {
    if (inicio.slice(0, 10) === limite.slice(0, 10)) return fechaCortaTarea(limite)
    return `${fechaCortaTarea(inicio)} – ${fechaCortaTarea(limite)}`
  }
  if (limite) return fechaCortaTarea(limite)
  if (inicio) return `Desde ${fechaCortaTarea(inicio)}`
  return ''
}

/** ¿Se pasó? Solo cuenta si la tarea sigue viva: lo terminado no vence. */
export function estaVencida(tarea: { estado: string | null; fecha_limite: string | null }): boolean {
  if (!tarea.fecha_limite) return false
  if (metaDeEstado(tarea.estado).terminal) return false
  return tarea.fecha_limite.slice(0, 10) < new Date().toISOString().slice(0, 10)
}
